import mockArtistWishlistData from "../data/mockData/artistWishlistData";

const STORAGE_EXTRAS = "wl_bs_artist_wishlist_extras";
const STORAGE_DELETED = "wl_bs_artist_wishlist_deleted";

function readJson(key, fallback) {
  if (typeof sessionStorage === "undefined") {
    return fallback;
  }
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

function mergeById(baseRows, extras) {
  const byId = new Map(baseRows.map(row => [String(row.id), row]));
  extras.forEach(row => {
    const id = String(row.id);
    byId.set(id, { ...byId.get(id), ...row });
  });
  return Array.from(byId.values());
}

function readDeletedIds() {
  return new Set((readJson(STORAGE_DELETED, []) || []).map(String));
}

function nextWishlistId(mergedRows) {
  return (mergedRows || []).reduce((max, row) => Math.max(max, Number(row.id) || 0), 0) + 1;
}

/** Merged seed + session wishlist rows, excluding demo deletes. */
export function getArtistWishlistRows() {
  const merged = mergeById(mockArtistWishlistData, readJson(STORAGE_EXTRAS, []));
  const deleted = readDeletedIds();
  return merged
    .filter(row => row && row.id != null && !deleted.has(String(row.id)))
    .sort((a, b) => Number(a.id) - Number(b.id));
}

export function getArtistWishlistById(id) {
  if (id == null || id === "") {
    return null;
  }
  return getArtistWishlistRows().find(row => String(row.id) === String(id)) || null;
}

export function findArtistWishlistBySpotifyIdentifier(spotifyIdentifier) {
  const sid = String(spotifyIdentifier || "").trim();
  if (!sid) {
    return null;
  }
  return (
    getArtistWishlistRows().find(row => String(row.spotify_identifier) === sid) || null
  );
}

export function findArtistWishlistIdBySpotifyIdentifier(spotifyIdentifier) {
  const row = findArtistWishlistBySpotifyIdentifier(spotifyIdentifier);
  return row ? row.id : null;
}

export function appendArtistWishlist(body) {
  const payload = body || {};
  const spotifyId = String(payload.spotify_identifier || "").trim();
  if (spotifyId) {
    const existing = findArtistWishlistBySpotifyIdentifier(spotifyId);
    if (existing) {
      return existing;
    }
  }

  const merged = mergeById(mockArtistWishlistData, readJson(STORAGE_EXTRAS, []));
  const id = nextWishlistId(merged);
  const row = {
    id,
    name: payload.name || "Unknown Artist",
    spotify_identifier: spotifyId,
    popularity: Number(payload.popularity) || 0,
    followers: Number(payload.followers) || 0,
    image_big: payload.image_big || "",
    image_small: payload.image_small || payload.image_big || "",
  };

  const extras = readJson(STORAGE_EXTRAS, []);
  extras.push(row);
  writeJson(STORAGE_EXTRAS, extras);
  return row;
}

export function removeArtistWishlist(id) {
  const sid = String(id);
  const extras = readJson(STORAGE_EXTRAS, []);
  const filtered = extras.filter(row => String(row.id) !== sid);
  if (filtered.length !== extras.length) {
    writeJson(STORAGE_EXTRAS, filtered);
    return true;
  }

  const deleted = readJson(STORAGE_DELETED, []);
  if (!deleted.map(String).includes(sid)) {
    deleted.push(Number(id) || id);
    writeJson(STORAGE_DELETED, deleted);
  }
  return true;
}

export function clearArtistWishlistSessionStorage() {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.removeItem(STORAGE_EXTRAS);
  sessionStorage.removeItem(STORAGE_DELETED);
}
